import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getProfileContributions, type ContributionSurface } from '@/api/users';

/** Lab contract check, not the Android Parichay design. */
export default function ProfileContributions({ viewerId }: { viewerId: string }) {
  const [input, setInput] = useState(viewerId);
  const [memberId, setMemberId] = useState(viewerId);
  const [surface, setSurface] = useState<ContributionSurface>('curious');
  const [cursor, setCursor] = useState<string>();
  const result = useQuery({
    queryKey: ['profile-contributions', viewerId, memberId, surface, cursor],
    queryFn: () => getProfileContributions(memberId, surface, cursor),
    retry: false,
    gcTime: 0,
    staleTime: 0,
  });
  // Do not show a cached page after a failed privacy recheck.
  const page = result.isError || result.isFetching ? undefined : result.data?.data;
  const paths: Record<string, string> = {
    question: '/lab/questions/', voice: '/lab/voices/', board: '/lab/nostalgia/boards/',
  };
  return (
    <section className="card p-6 mb-6 space-y-4" aria-label="Member contributions">
      <h2 className="text-lg font-semibold">Member contributions</h2>
      <p className="text-sm text-gray-600">Only public, attributed activity is listed. Profile privacy and blocks apply, including to later pages.</p>
      <form className="flex gap-2" onSubmit={(event) => {
        event.preventDefault();
        setCursor(undefined);
        setMemberId(input.trim());
        if (input.trim() === memberId && cursor === undefined) void result.refetch();
      }}>
        <label className="flex-1">Member ID
          <input required className="input w-full" value={input} onChange={(event) => setInput(event.target.value)} />
        </label>
        <button className="btn-outline" type="submit">Load member</button>
      </form>
      <label className="block">Surface
        <select className="input ml-2" value={surface} onChange={(event) => {
          setSurface(event.target.value as ContributionSurface); setCursor(undefined);
        }}>
          <option value="curious">Curious</option>
          <option value="awaaz">Awaaz</option>
          <option value="yaadein">Yaadein</option>
        </select>
      </label>
      {result.isFetching && <p role="status">Loading contributions…</p>}
      {result.isError && <p role="alert">{result.error.message} Reload the member to start again.</p>}
      {page && page.items.length === 0 && <p>No visible contributions on this surface.</p>}
      {page && <ul className="divide-y">{page.items.map((item) => (
        <li key={`${item.kind}:${item.id}`} className="py-3">
          <div className="text-sm text-gray-500">{item.kind} · {new Date(item.createdAt).toLocaleDateString()}</div>
          {paths[item.target.kind]
            ? <Link className="text-primary-600 underline" to={`${paths[item.target.kind]}${encodeURIComponent(item.target.id)}`}>{item.title}</Link>
            : <span>{item.title} — detail type not supported</span>}
        </li>
      ))}</ul>}
      <div className="flex gap-2">
        <button className="btn-outline" disabled={result.isFetching} onClick={() => {
          if (cursor) setCursor(undefined); else void result.refetch();
        }}>Reload first page</button>
        {page?.nextCursor && <button className="btn-primary" onClick={() => setCursor(page.nextCursor!)}>Next page</button>}
      </div>
    </section>
  );
}
